import React, { Fragment } from 'react';

import t from '../../../labels/labelling-utils';
import { MatchStatusType } from '../../../typings/match';
import { IReportsDetailsMatch } from '../../../typings/match-reports';
import { IPlayer, IReferee } from '../../../typings/player';
import { PanelVariantType, PREDICTEDLINEUP, PREDICTEDLINEUPSLIM, REPORTS } from '../../../typings/report-tabs';
import { makeClassNames } from '../../../utils/styles';
import Text from '../../atoms/text/text';
import MatchFormationPlayer from '../match-formation-player/match-formation-player';
import ReportPlayer from '../report-player/report-player';
import { ITeam } from '../team/team';

import './report-tabs-panel.scss';

const formatPlayerWithJerseyNum = (player: IPlayer): string | null | undefined => {
  if (!player) {
    return null;
  }

  const { fullname, jerseyNum } = player;
  if (!jerseyNum) {
    return fullname;
  }

  return `${jerseyNum}. ${fullname}`;
};

export interface IReportTabsPanel extends Omit<IReportsDetailsMatch, 'home' | 'away' | 'referees' | 'status'> {
  away?: ITeam;
  home?: ITeam;
  panelVariant?: PanelVariantType;
  referees?: IReferee[];
  status?: MatchStatusType;
}
interface IGroupList {
  groupName: string;
  home: IPlayer[];
  away: IPlayer[];
  referees?: IReferee[];
  isArrayJoined: boolean;
  isMain?: boolean;
}

const classNames = (
  isHomeOrAwayEmpty: boolean,
  isHomeAndAway: boolean,
  away: ITeam | undefined,
  home: ITeam | undefined
) =>
  makeClassNames({
    reportsPanel: 'report-tabs-panel',
    header: 'report-tabs-panel__header',
    body: 'report-tabs-panel__body',
    td: 'report-tabs-panel__header-td',
    col: ['report-tabs-panel__col', isHomeOrAwayEmpty && 'report-tabs-panel__col--full'],
    tdHomeFull: ['report-tabs-panel__td', !away && 'report-tabs-panel__td--full'],
    tdJoinedAway: [
      'report-tabs-panel__td',
      'report-tabs-panel__joined-array',
      isHomeAndAway && 'report-tabs-panel__td--right',
      !home && 'report-tabs-panel__td--full',
    ],
    tdJoinedHome: [
      'report-tabs-panel__td',
      'report-tabs-panel__joined-array',
      isHomeAndAway && 'report-tabs-panel__td--right',
      !away && 'report-tabs-panel__td--full',
    ],
    tdAwayFull: [
      'report-tabs-panel__td',
      // isHomeAndAway && 'report-tabs-panel__td--right', -> AWAY/RIGHT column should be more "left aligned" towards the center of the page
      !home && 'report-tabs-panel__td--full',
    ],
    colFull: 'report-tabs-panel__col report-tabs-panel__col--full',
    tdFull: ['report-tabs-panel__td', 'report-tabs-panel__td--full'],
    placeholder: 'report-tabs-panel__placeholder',
  });

const ReportTabsPanel = ({
  home,
  away,
  /* istanbul ignore next: default value */ referees = [],
  /* istanbul ignore next: default value */ panelVariant = REPORTS,
}: IReportTabsPanel) => {
  const groupList: IGroupList[] = [
    {
      groupName: t('report.tabs.panel.starting'),
      home: home?.playerList?.startingLineup || [],
      away: away?.playerList?.startingLineup || [],
      isArrayJoined: false,
      isMain: true,
    },
    {
      groupName:
        panelVariant === REPORTS ? t('report.tabs.panel.substitution') : t('formation.tabs.panel.substitution'),
      home: home?.playerList?.substitutes || [],
      away: away?.playerList?.substitutes || [],
      isArrayJoined: [PREDICTEDLINEUP, PREDICTEDLINEUPSLIM].includes(panelVariant),
    },
    {
      groupName: t('report.tabs.panel.coach'),
      home: home?.playerList?.manager ? [home.playerList.manager] : [],
      away: away?.playerList?.manager ? [away.playerList.manager] : [],
      isArrayJoined: [PREDICTEDLINEUP, PREDICTEDLINEUPSLIM].includes(panelVariant),
      isMain: true,
    },
    {
      groupName: panelVariant === REPORTS ? t('report.tabs.panel.referees') : t('formation.tabs.panel.referees'),
      home: [],
      away: [],
      isArrayJoined: false,
      referees,
    },
  ];

  const predictedLineupAddons: IGroupList[] = [
    {
      groupName: t('report.tabs.panel.potential.players'),
      home: home?.playerList?.potentialPlayers ?? [],
      away: away?.playerList?.potentialPlayers ?? [],
      isArrayJoined: true,
    },
    {
      groupName: t('report.tabs.panel.disqualifieds'),
      home: home?.playerList?.disqualifieds ?? [],
      away: away?.playerList?.disqualifieds ?? [],
      isArrayJoined: true,
    },
    {
      groupName: t('report.tabs.panel.unavailables'),
      home: home?.playerList?.unavailables ?? [],
      away: away?.playerList?.unavailables ?? [],
      isArrayJoined: true,
    },
  ];

  const groupListWithoutCoach: IGroupList[] = groupList.slice(0, 2).concat(groupList[3]);

  const predictedLineupGroupList: IGroupList[] = groupListWithoutCoach
    .concat(predictedLineupAddons, groupList[2])
    .filter((item) => {
      const refObj = groupList.find((obj: IGroupList) => obj.referees);
      return item !== refObj;
    });

  const predictedLineupSlimList: IGroupList[] = predictedLineupGroupList.filter((item) => {
    return item.groupName !== t('report.tabs.panel.starting');
  });

  const isHomeAndAway = Boolean(home && away);
  const isHomeOrAwayEmpty = Boolean(!home || !away);

  const cn = classNames(isHomeOrAwayEmpty, isHomeAndAway, away, home);

  const placeholder = <div className={cn.placeholder} />;

  const getGroupList = () => {
    switch (panelVariant) {
      case PREDICTEDLINEUP:
        return predictedLineupGroupList;
      case PREDICTEDLINEUPSLIM:
        return predictedLineupSlimList;
      default:
        return groupList;
    }
  };

  const shouldRenderNotMainGroupAsMain = (isMain?: boolean) =>
    [PREDICTEDLINEUP, PREDICTEDLINEUPSLIM].includes(panelVariant) ? Boolean(isMain) : true;

  return (
    <table className={cn.reportsPanel} data-panels>
      <tbody className={cn.body}>
        {/* FIXME a lots of any types  and key={index} */}
        {getGroupList().map((group, i: number) => {
          let maxLength = 1;
          let homeTeam: any[] = [];
          let awayTeam: any[] = [];
          if (group.home && group.away) {
            // Tests reported a NaN array length, so...
            const homeLength = group.home.length || 0;
            const awayLength = group.away.length || 0;
            maxLength = Math.max(homeLength, awayLength) || 0;
            homeTeam = [...group.home, ...Array.from(Array(maxLength - homeLength))];
            awayTeam = [...group.away, ...Array.from(Array(maxLength - awayLength))];
          }

          const joinHomeTeamArray = homeTeam.map(formatPlayerWithJerseyNum).filter(Boolean).join(', ') || '-';

          const joinAwayTeamArray = awayTeam.map(formatPlayerWithJerseyNum).filter(Boolean).join(', ') || '-';

          return (
            <Fragment key={i}>
              {(homeTeam.length + awayTeam.length > 0 || group.referees) && (
                <tr className={cn.header}>
                  <td colSpan={maxLength} className={cn.td}>
                    {group.groupName}
                  </td>
                </tr>
              )}
              {home && (
                <tr className={cn.col} data-panel-left>
                  {!group.isArrayJoined &&
                    homeTeam.map((player: IPlayer | undefined, teamIndex) => (
                      <td key={teamIndex} className={cn.tdHomeFull}>
                        {player && home ? (
                          <Fragment>
                            {panelVariant === REPORTS ? (
                              <ReportPlayer {...player} />
                            ) : (
                              <Fragment>
                                {shouldRenderNotMainGroupAsMain(group.isMain) && (
                                  <MatchFormationPlayer
                                    {...player}
                                    goals={home.goals}
                                    scorersCards={home.scorersCards}
                                    substitution={home.substitution}
                                    topPlayerId={home.topPlayerId}
                                  />
                                )}
                              </Fragment>
                            )}
                          </Fragment>
                        ) : (
                          placeholder
                        )}
                      </td>
                    ))}
                  {group.isArrayJoined && home && (
                    <td className={cn.tdJoinedHome}>
                      <Text fontSize="16--14" color="dark">
                        {joinHomeTeamArray}
                      </Text>
                    </td>
                  )}
                </tr>
              )}
              {away && (
                <tr className={cn.col} data-panel-right>
                  {!group.isArrayJoined &&
                    awayTeam.map((player: IPlayer | undefined, teamIndex) => (
                      <td key={teamIndex} className={cn.tdAwayFull}>
                        {player && away ? (
                          <Fragment>
                            {panelVariant === REPORTS ? (
                              <ReportPlayer {...player} />
                            ) : (
                              <Fragment>
                                {shouldRenderNotMainGroupAsMain(group.isMain) && (
                                  <MatchFormationPlayer
                                    {...player}
                                    goals={away.goals}
                                    scorersCards={away.scorersCards}
                                    substitution={away.substitution}
                                    topPlayerId={away.topPlayerId}
                                  />
                                )}
                              </Fragment>
                            )}
                          </Fragment>
                        ) : (
                          placeholder
                        )}
                      </td>
                    ))}
                  {group.isArrayJoined && away && (
                    <td className={cn.tdJoinedAway}>
                      <Text fontSize="16--14" color="dark">
                        {joinAwayTeamArray}
                      </Text>
                    </td>
                  )}
                </tr>
              )}
              {group.referees && group.referees.length > 0 && (
                <tr className={cn.colFull}>
                  {group.referees.map((player: IPlayer, j: number) => (
                    <td key={j} className={cn.tdFull}>
                      {<ReportPlayer {...player} />}
                    </td>
                  ))}
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

export default ReportTabsPanel;
